import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { MatSelectChange } from '@angular/material/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  MunilySelectFieldOption,
  MunilyTextFieldAppearance,
  MunilyTextFieldErrorMessages,
  MunilyTextFieldSize,
  MunilyTextFieldWidthLayout,
} from './select-field.types';

@Component({
  selector: 'app-select-field',
  templateUrl: './select-field.component.html',
  styleUrls: ['./select-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control?: FormControl;

  @Input() appearance: MunilyTextFieldAppearance = 'outlined';
  @Input() size: MunilyTextFieldSize = 'default';
  @Input() widthLayout: MunilyTextFieldWidthLayout = 'ds';
  @Input() maxWidth?: string;

  /** Opciones del desplegable (valor + label o labelKey para i18n). */
  @Input() options: MunilySelectFieldOption[] = [];

  /** Selección múltiple (mat-select multiple). */
  @Input() multiple = false;

  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() iconLeading?: string;

  /** Clase del overlay del panel (estilos globales del proyecto). */
  @Input() panelClass = 'custom-select-panel';

  @Input() disableOptionCentering = true;

  /** Comparación de valores (útil si `value` son objetos). */
  @Input() compareWith?: (a: unknown, b: unknown) => boolean;

  @Input() floatLabel: FloatLabelType = 'auto';
  @Input() disabled = false;
  @Input() successful = false;

  @Input() errorMessage = '';
  @Input() errorMessages: MunilyTextFieldErrorMessages = {};

  @Output() selectionChange = new EventEmitter<MatSelectChange>();
  @Output() valueChange = new EventEmitter<unknown>();

  @HostBinding('class.munily-sf-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  readonly fallbackControl = new FormControl(null);
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  get materialAppearance(): 'outline' | 'fill' {
    return this.appearance === 'filled' ? 'fill' : 'outline';
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this.firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this.syncDisabled();
    this.wireControlUpdates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.syncDisabled();
    }
    if (changes['control']) {
      this.destroy$.next();
      this.syncDisabled();
      this.wireControlUpdates();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  onSelectionChange(ev: MatSelectChange): void {
    this.selectionChange.emit(ev);
    this.valueChange.emit(ev.value);
  }

  onOpenedChange(open: boolean): void {
    if (!open) {
      this.resolvedControl.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  private syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  private wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
  }

  private firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
